'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { clearProductListingFilterParams } from '@/utils/product-listing-search-params';
import { STUDIO_CATEGORY_QUERY_PARAM } from '@/utils/studio-preview-locale-url';

let lastListingKey: string | undefined;

const getListingKey = (
  pathname: string,
  searchParams: URLSearchParams,
  isStudioPreview: boolean,
) => {
  if (!isStudioPreview) {
    return pathname;
  }

  return `${pathname}?${STUDIO_CATEGORY_QUERY_PARAM}=${searchParams.get(STUDIO_CATEGORY_QUERY_PARAM) ?? ''}`;
};

/**
 * Drops facet/page query params when moving between product listings (category
 * ↔ category, category ↔ all-products). First load and in-listing filter
 * changes keep the current URL.
 */
const useResetProductListingFiltersOnNavigate = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useProductListingSearchParams();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>() ?? {};
  const listingKey = getListingKey(pathname, searchParams, Boolean(isStudioPreview));

  useEffect(() => {
    const previousKey = lastListingKey;
    lastListingKey = listingKey;

    if (previousKey === undefined || previousKey === listingKey) {
      return;
    }

    const next = clearProductListingFilterParams(searchParams, {
      isStudioPreview: Boolean(isStudioPreview),
    });
    next.delete('page');

    if (next.toString() === searchParams.toString()) {
      return;
    }

    const query = next.toString();
    router.replace(query ? `${pathname}?${query}` : pathname);
  }, [isStudioPreview, listingKey, pathname, router, searchParams]);
};

export default useResetProductListingFiltersOnNavigate;
